export const SUPPORTED_LOCALES = ["en", "zh-Hans", "zh-Hant"] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = "wyni.ui.locale";

export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: "English",
  "zh-Hans": "简体中文",
  "zh-Hant": "繁體中文",
};

export const isAppLocale = (value: string | null | undefined): value is AppLocale =>
  Boolean(value && SUPPORTED_LOCALES.includes(value as AppLocale));

const normalizeBrowserLocale = (language: string): AppLocale | null => {
  const normalized = language.trim().toLowerCase();

  if (normalized.startsWith("zh")) {
    if (
      normalized.includes("hant") ||
      normalized.includes("tw") ||
      normalized.includes("hk") ||
      normalized.includes("mo")
    ) {
      return "zh-Hant";
    }
    return "zh-Hans";
  }

  if (normalized.startsWith("en")) {
    return "en";
  }

  return null;
};

export const detectBrowserLocale = (): AppLocale => {
  if (typeof navigator === "undefined") {
    return "en";
  }

  const candidates = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const locale = normalizeBrowserLocale(candidate);
    if (locale) {
      return locale;
    }
  }

  return "en";
};

export const readStoredLocale = (): AppLocale | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isAppLocale(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const persistLocale = (locale: AppLocale): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }

  document.documentElement.lang = locale;
};

export const resolveInitialLocale = (): AppLocale =>
  readStoredLocale() ?? detectBrowserLocale();
